<template>
  <div class="row wrapper border-bottom white-bg page-heading">
    <div class="col-lg-10">
      <h2>Baza kosztów stałych</h2>
      <ol class="breadcrumb">
        <li>
          <router-link to="/">Home</router-link>
        </li>
        <li class="active">
          <strong>Baza kosztów stałych</strong>
        </li>
      </ol>
    </div>
  </div>

  <div class="wrapper wrapper-content animated fadeInRight">
    <div class="row">
      <div class="col-lg-12">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Koszty stałe</h5>
          </div>
          <div class="ibox-content">
            <router-link v-if="canAddCost" to="/costs/create">
              <button class="btn btn-sm btn-primary">Dodaj</button>
            </router-link>
            <div class="row">
              <div class="col-sm-12 m-b-xs">
                <div class="form-group">
                  <label class="control-label col-sm-2">
                    Wpisz liczbę wierszy:
                  </label>
                  <div class="col-sm-2">
                    <input
                      class="form-control inputClass"
                      type="text"
                      v-model="rowsPerPage"
                      @change="setRowsPerPage"
                      @click="isSearch = false"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-sm-12 m-b-xs" v-if="items">
                <button @click="resetFilters" class="btn btn-primary btn-xs">
                  <i class="fa fa-refresh"></i>
                </button>
                <div class="pagination" v-if="itemsAll">
                  <span class="items-all">
                    Ilość rekordów: <b>{{ itemsAll }}</b>
                  </span>
                  <span>
                    Ilość na stronę:
                    <select v-model="serverOptions.rowsPerPage">
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </span>
                  <span>
                    <input type="number" v-model="serverOptions.page" /> -
                    {{ Math.ceil(itemsAll / serverOptions.rowsPerPage) }}
                  </span>
                  <button @click="selectPage(1)" class="btn btn-primary ml-25">
                    Pierwsza
                  </button>
                  <button
                    @click="serverOptions.page = serverOptions.page - 1"
                    :disabled="serverOptions.page === 1"
                    class="prev nl"
                  ></button>
                  <button
                    @click="serverOptions.page = serverOptions.page + 1"
                    :disabled="
                      serverOptions.page ===
                      Math.ceil(itemsAll / serverOptions.rowsPerPage)
                    "
                    class="next"
                  ></button>
                  <button
                    @click="
                      selectPage(
                        Math.ceil(itemsAll / serverOptions.rowsPerPage)
                      )
                    "
                    class="btn btn-primary"
                  >
                    Ostatnia
                  </button>
                </div>
                <EasyDataTable
                  v-model:server-options="serverOptions"
                  :server-items-length="itemsAll"
                  :headers="headers"
                  :items="items"
                  :loading="loading"
                  alternating
                  multi-sort
                  border-cell
                  class="table-search"
                  show-index
                >
                  <template #header="header">
                    <span v-if="header.text">{{ header.text }}</span>
                    <input
                      class="table-search"
                      type="text"
                      v-if="header.search"
                      v-model="searchValue[header.value]"
                      @keyup="search"
                      @click="isSearch = true"
                    />
                  </template>
                  <template #item-actions="item">
                    <button
                      v-if="canChangeCost"
                      @click="editCost(item)"
                      class="btn btn-warning btn-xs"
                    >
                      <i class="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                    <button
                      v-if="canDeleteCost"
                      @click="deleteCost(item)"
                      class="btn btn-danger btn-xs"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </template>
                  <template #item-value="{ value }">
                    {{ formatCurrency(value) }}
                  </template>
                  <template #item-period_unit="{ value }">
                    {{ getPeriodUnitLabel(value) }}
                  </template>
                </EasyDataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Section -->
    <div class="row">
      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Podsumowanie miesięczne</h5>
          </div>
          <div class="ibox-content">
            <h2 class="text-success">
              {{ formatCurrency(summaryData.monthly_cost) }}
            </h2>
          </div>
        </div>
      </div>
      <div class="col-lg-6">
        <div class="ibox float-e-margins">
          <div class="ibox-title">
            <h5>Podsumowanie roczne</h5>
          </div>
          <div class="ibox-content">
            <h2 class="text-info">
              {{ formatCurrency(summaryData.annual_cost) }}
            </h2>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Modal
    v-if="showModal"
    :title="'Potwierdzenie usunięcia'"
    :content="'Czy na pewno chcesz usunąć <b>' + deleteName + '</b> ?'"
    @callbackOk="deleteConf(showModal)"
    @callbackClose="showModal = false"
  />
</template>

<script>
import { computed, watch, ref, onMounted } from "vue";
import { useStore } from "vuex";
import { hasPermission } from "../../permissions";
import { costPermissionsConst } from "../../constants/costConstants";
import { useRouter, useRoute } from "vue-router";
import Modal from "../caffeprint/Modal.vue";

export default {
  name: "CostsList",
  mixins: [],
  components: {
    Modal,
  },
  props: {},

  setup() {
    const store = useStore();
    const canAddCost = computed(() => hasPermission(costPermissionsConst.add));
    const canChangeCost = computed(() =>
      hasPermission(costPermissionsConst.update)
    );
    const canDeleteCost = computed(() =>
      hasPermission(costPermissionsConst.delete)
    );
    const router = useRouter();
    const route = useRoute();
    const allCostsData = computed(() => store.getters.getAllCostsData);
    const allCostsDeleteData = computed(
      () => store.getters.getAllCostsDeleteData
    );
    const allCostsSummaryData = computed(
      () => store.getters.getAllCostsSummaryData
    );
    const unitsData = computed(() => store.getters.getUnitsData);
    const periodUnitOptsData = computed(
      () => store.getters.getPeriodUnitOptsData
    );

    let headers = [
      { text: "ID", value: "pk" },
      { text: "Nazwa", value: "name", sortable: true, search: true },
      { text: "Wartość", value: "value" },
      { text: "Jednostka", value: "unit.symbol" },
      { text: "Okres", value: "period", sortable: true },
      { text: "Jednostka okresu", value: "period_unit" },
      { text: "Data dodania", value: "date_added", sortable: true },
      { text: "Data modyfikacji", value: "date_modified", sortable: true },
      {
        text: "Zmodyfikowano przez",
        value: "last_modified_by",
        sortable: false,
      },
      {
        text: "Utworzono przez",
        value: "created_by",
        sortable: false,
      },
      { text: "Akcje", value: "actions" },
    ];

    let items = ref([]);
    let itemsAll = ref(0);
    const showModal = ref(false);
    const deleteName = ref("");
    const loading = ref(true);
    const searchValue = ref({});
    const isSearch = ref(false);
    const rowsPerPage = ref(25);
    const serverOptions = ref({
      page: 1,
      rowsPerPage: 25,
      sortBy: "pk",
      sortType: "desc",
    });
    const summaryData = ref({
      monthly_cost: 0,
      annual_cost: 0,
    });

    const getAllCosts = async () => {
      loading.value = true;
      try {
        let ordering = serverOptions.value.sortBy || "pk";
        if (serverOptions.value.sortType === "desc") {
          ordering = "-" + ordering;
        }
        const payload = {
          page: serverOptions.value.page,
          rowsPerPage: serverOptions.value.rowsPerPage,
          ordering,
          search: searchValue.value,
        };
        await store.dispatch("fetchCosts", payload);
        await store.dispatch("fetchCostsSummary");
        await store.dispatch("fetchUnits");
        await store.dispatch("fetchPeriodUnitOpts");
      } catch (error) {
        console.error("Error fetching costs:", error);
      } finally {
        loading.value = false;
      }
    };

    const editCost = (item) => {
      if (!canChangeCost.value) {
        return;
      }
      router.push(`/costs/edit/${item.pk}`);
    };

    const deleteCost = (item) => {
      deleteName.value = item.name;
      showModal.value = item.pk;
    };

    const deleteConf = async (pk) => {
      if (!canDeleteCost.value) {
        return;
      }
      try {
        await store.dispatch("deleteCost", pk);
        showModal.value = false;
        await getAllCosts();
        store.dispatch("showToaster", {
          type: "success",
          content: "Koszt stały został usunięty",
        });
      } catch (error) {
        console.error("Error deleting cost:", error);
        store.dispatch("showToaster", {
          type: "error",
          content: "Błąd podczas usuwania kosztu stałego",
        });
      }
    };

    const resetFilters = () => {
      searchValue.value = {};
      serverOptions.value.sortBy = "pk";
      serverOptions.value.sortType = "desc";
      serverOptions.value.page = 1;
      getAllCosts();
    };

    const selectPage = (page) => {
      serverOptions.value.page = page;
      getAllCosts();
    };

    const setRowsPerPage = () => {
      serverOptions.value.rowsPerPage = parseInt(rowsPerPage.value);
      serverOptions.value.page = 1;
      getAllCosts();
    };

    const search = () => {
      isSearch.value = true;
      if (searchValue.value) {
        selectPage(1);
        setTimeout(() => {
          getAllCosts();
          isSearch.value = false;
        }, 1000);
      }
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
      }).format(value);
    };

    const getPeriodUnitLabel = (value) => {
      const periodUnits = periodUnitOptsData.value?.data?.data || [];
      const unit = periodUnits.find((u) => u.value === value);
      return unit ? unit.name : value;
    };

    const getUnitLabel = (value) => {
      const units =
        unitsData.value?.data?.results || unitsData.value?.data || [];
      const unit = units.find((u) => u.pk === value);
      return unit ? unit.name : value;
    };

    watch(allCostsData, (newData) => {
      if (newData?.data) {
        items.value = newData.data.results || [];
        itemsAll.value = newData.data.count || 0;
      }
    });

    watch(allCostsSummaryData, (newData) => {
      if (newData?.data) {
        summaryData.value = newData.data;
      }
    });

    // Refresh when coming back with r query param or when route changes
    watch(
      () => route.query.r,
      () => {
        getAllCosts();
      }
    );

    // React to page/size/sort changes from table
    watch(
      () => serverOptions.value,
      function () {
        if (!isSearch.value) {
          getAllCosts();
          isSearch.value = false;
        }
      },
      { deep: true }
    );

    onMounted(() => {
      getAllCosts();
    });

    return {
      canAddCost,
      canChangeCost,
      canDeleteCost,
      allCostsData,
      allCostsDeleteData,
      allCostsSummaryData,
      unitsData,
      periodUnitOptsData,
      headers,
      items,
      itemsAll,
      showModal,
      deleteName,
      loading,
      searchValue,
      isSearch,
      rowsPerPage,
      serverOptions,
      summaryData,
      getAllCosts,
      editCost,
      deleteCost,
      deleteConf,
      resetFilters,
      selectPage,
      setRowsPerPage,
      search,
      formatCurrency,
      getPeriodUnitLabel,
      getUnitLabel,
    };
  },
};
</script>

<style scoped>
.ml-25 {
  margin-left: 25px;
}
</style>
